import { isWeb } from 'tamagui';
import CssFilterConverter from 'css-filter-converter';
import type { ColorToFilterResult } from 'css-filter-converter/lib/shared/types/result';

const color = {
  // Main theme
  black: '#000',
  white: '#fff',
  tzaGreen: '#85C19D',

  // Light colors
  lightGrey: '#6C757D',
  dullGrey: '#E9ECEF',
  opaqueGrey:'#959595',
  lightGreen: '#A5D6B7',
  opaqueGreen: 'rgba(99, 153, 93, 0.15)',
  lightRed: '#FF6B6B',
  opaqueRed: 'rgba(255, 107, 107, 0.15)',
  lightLavender: '#E0E0E0',
  opaqueBlack: '#C6C6C6',

  // Dark colors
  grey: '#495057',
  darkGrey: 'rgb(74, 76, 79)',
  green: '#8BC34A',
  cyan: '#4ECDC4',
  darkSkyBlue: '#5DADE2',
  parrotGreen: '#15B100',
  blue:'#3498DB',
  toggleGreen:'#7DCEA0',
  titleBlack: 'rgb(73, 80, 87)',
  darkNavy: 'rgb(27, 45, 75)',
} as const;  

const extendedColorPalette = [
  color.lightRed,        // light red
  color.tzaGreen,        // tza green
  color.cyan,            // cyan
  color.grey,            // grey
  color.green,           // green
  color.blue,            // blue
  color.darkSkyBlue,     // dark sky blue
  color.parrotGreen,     // parrot green
  color.toggleGreen,     // toggle green
  color.lightGrey,       // light grey
  color.opaqueGrey,      // opaque grey
  color.lightGreen,      // light green
  color.opaqueGreen,     // opaque green
  color.lightLavender,   // light lavender
  color.opaqueBlack,     // opaque black
  '#E74C3C',             // dark red
  '#27AE60',             // dark green
  '#2980B9',             // darker blue
  '#8E44AD',             // purple
  '#34495E',             // dark grey-blue
  '#1ABC9C',             // turquoise
  '#9B59B6',             // dark lavender
  '#F39C12',             // dark yellow
  '#D35400',             // dark orange
  '#BDC3C7',             // light grey-blue
  '#16A085',             // teal
  '#F1C40F',             // bright yellow
  '#C0392B',             // strong red
  '#7F8C8D',              // medium grey
  color.titleBlack
] 

const brandGuidelinesColors = {
  primaryGreen: color.tzaGreen,
  primaryGreenAlt: '#3A4B4D',
  secondaryYellow: '#FFD336',
  secondaryMustard: '#FBD53D',
  secondaryViolet: '#9400D3',
  secondaryPinkLavender: '#D2B4DE',
  secondaryBlue: '#39A6EF',
  secondaryRobinEggBlue: '#4ECDC4',
  secondarySalmon: '#FF8075',
  secondaryBrightBlue: '#49B6D4',
  tertiaryCornSilk: '#FFF3D6',
  tertiaryNonPhotoBlue: '#9CD7E8',
  tertiaryPlatinum: '#CFDDD7',
} as const;

const brandChartColors: Array<keyof typeof brandGuidelinesColors> = [
  'secondaryBlue', 'secondarySalmon', 'secondaryMustard', 'secondaryRobinEggBlue', 'secondaryPinkLavender', 'primaryGreen'
]

const GoogleChartColors = [
  "#3366CC",
  "#DC3912",
  "#FF9900",
  "#109618",
  "#990099",
  "#3B3EAC",
  "#0099C6",
  "#DD4477",
  "#66AA00",
  "#B82E2E",
  "#316395",
  "#994499",
  "#22AA99",
  "#AAAA11",
  "#6633CC",
  "#E67300",
  "#8B0707",
  "#329262",
  "#5574A6",
  "#3B3EAC"
];

const TZABrandGuidelines = {
  colorsMap: brandGuidelinesColors,
  colorsArray: Object.values(brandGuidelinesColors),
  chartColors: brandChartColors.map(key => brandGuidelinesColors[key]),
}

const getImageColorStyle = (color = 'black') => {
  const blackout = 'brightness(0) saturate(100%)';

  if (!isWeb) {
    // Native (Expo) uses tintColor which works natively with all CSS colors
    return { tintColor: color };
  }

  // Web Implementation (Filters)
  try {
    const input = color.trim().toLowerCase();
    let result: ColorToFilterResult;

    if (input.startsWith('#')) {
      result = CssFilterConverter.hexToFilter(input);
    } else if (input.startsWith('rgb')) {
      result = CssFilterConverter.rgbToFilter(input);
    } else if (input.startsWith('hsl')) {
      result = CssFilterConverter.hslToFilter(input);
    } else {
      // Handles 'red', 'blue', etc.
      result = CssFilterConverter.keywordToFilter(input);
    }

    // Failsafe: if library fails or color is invalid, return black silhouette
    if (!result || !result.color) return { filter: blackout };

    return { filter: `${blackout} ${result.color}` };
  } catch (e) {
    // Ultimate failsafe
    return { filter: blackout };
  }
};

export { color, extendedColorPalette, TZABrandGuidelines, getImageColorStyle };


